/**
 * index.tsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Import root app
// Initialize languages
import './locales/i18n';

import Env from 'env';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import 'sanitize.css/sanitize.css';
const theme = Env.theme;

import(`./styles/theme/${theme}/theme.scss`);

import * as React from 'react';

import { QueryClientProvider } from '@tanstack/react-query';
import { OidcProvider } from 'app/core/security/OidcProvider';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { ToastContainer, Zoom } from 'react-toastify';

import { queryClient } from './app/core/query/queryClient';
import axiosConfigurations from './app/http/interceptors/configure-interceptors';
import { App } from './app';

const MOUNT_NODE = document.getElementById('root') as HTMLElement;

interface Props {
  Component: typeof App;
}

if (Env.profile === 'production') {
  console.log = () => {};
}

const ConnectedApp = ({ Component }: Props) => {
  React.useEffect(() => {
    axiosConfigurations.ConfigureAxiosInterceptors();
  }, []);

  return (
    <OidcProvider>
      <>
        <QueryClientProvider client={queryClient}>
          <HelmetProvider>
            <React.StrictMode>
              <Component />
            </React.StrictMode>
          </HelmetProvider>
        </QueryClientProvider>
        <ToastContainer autoClose={2000} transition={Zoom} />
      </>
    </OidcProvider>
  );
};

const root = createRoot(MOUNT_NODE);
const render = (Component: typeof App) => {
  root.render(<ConnectedApp Component={Component} />);
};

if (import.meta.hot) {
  // Hot reloadable translation json files and app
  import.meta.hot.accept(['./app', './locales/i18n'], async () => {
    const mod = await import('./app');
    render(mod.App);
  });
}

render(App);
